import { useRef } from "react";

import {
  Chart as ChartJS,
  BarController,
  BarElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend
} from "chart.js";

import zoomPlugin from "chartjs-plugin-zoom";
import { Bar } from "react-chartjs-2";
import "chartjs-adapter-date-fns";

import ChartToolbar from "./ChartToolbar";


// Register the Chart.js components and zoom plugin used by this chart.
ChartJS.register(
  BarController,
  BarElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  zoomPlugin
);


function AverageTimeChart({
  data = [],
  grouping = "daily",
  chargerType = "Both"
}) {
  const chartRef = useRef(null);


  const AC_COLOUR = "#2e7d32";
  const DC_COLOUR = "#f9a825";


  // Check that each row contains a valid date and numeric value.
  const isValidRow = (row) => {
    const date = new Date(row.date);
    const value = Number(row.value);

    return (
      !Number.isNaN(date.getTime()) &&
      date.getFullYear() >= 2016 &&
      !Number.isNaN(value)
    );
  };


  // Determine which charger types should be displayed.
  const showAC = chargerType === "Both" || chargerType === "AC";
  const showDC = chargerType === "Both" || chargerType === "DC";


  // Convert AC data into Chart.js x/y points.
  const acData = data
    .filter(
      (row) =>
        row.chargerType === "AC" &&
        showAC &&
        isValidRow(row)
    )
    .map((row) => ({
      x: new Date(row.date).getTime(),
      y: Number(row.value)
    }));


  // Convert DC data into Chart.js x/y points.
  const dcData = data
    .filter(
      (row) =>
        row.chargerType === "DC" &&
        showDC &&
        isValidRow(row)
    )
    .map((row) => ({
      x: new Date(row.date).getTime(),
      y: Number(row.value)
    }));


  // Match the chart's time scale to the selected grouping.
  const getTimeUnit = () => {
    if (grouping === "monthly") return "month";
    if (grouping === "yearly") return "year";
    return "day";
  };


  // Create datasets based on the selected charger type.
  const datasets = [];


  if (showAC) {
    datasets.push({
      label: "AC",
      data: acData,
      backgroundColor: AC_COLOUR,
      borderColor: AC_COLOUR,
      borderWidth: 1,
      barPercentage: 0.95,
      categoryPercentage: 0.9,
      maxBarThickness:
        grouping === "daily"
          ? 24
          : grouping === "monthly"
            ? 42
            : 55
    });
  }


  if (showDC) {
    datasets.push({
      label: "DC",
      data: dcData,
      backgroundColor: DC_COLOUR,
      borderColor: DC_COLOUR,
      borderWidth: 1,
      barPercentage: 0.95,
      categoryPercentage: 0.9,
      maxBarThickness:
        grouping === "daily"
          ? 24
          : grouping === "monthly"
            ? 42
            : 55
    });
  }


  const chartData = {
    datasets
  };


  // Configure the chart axes, legend, tooltips and zoom behaviour.
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    parsing: false,
    animation: false,
    interaction: {
      mode: "index",
      intersect: false
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: getTimeUnit()
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 20,
          maxRotation: 45,
          minRotation: 0
        },
        title: {
          display: true,
          text: "Date"
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Average Time (minutes)"
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: "right",
        labels: {
          boxWidth: 14,
          boxHeight: 14,
          padding: 14
        }
      },
      tooltip: {
        mode: "index",
        intersect: false
      },

      // Allow horizontal zooming and panning across the date range.
      zoom: {
        limits: {
          x: {
            min: "original",
            max: "original",
            minRange:
              grouping === "yearly"
                ? 31536000000
                : grouping === "monthly"
                  ? 2592000000
                  : 86400000
          }
        },
        pan: {
          enabled: true,
          mode: "x",
          modifierKey: "shift"
        },
        zoom: {
          mode: "x",
          wheel: {
            enabled: true,
            modifierKey: "ctrl",
            speed: 0.08
          },
          pinch: {
            enabled: true
          },
          drag: {
            enabled: true,
            modifierKey: "shift",
            borderWidth: 1
          }
        }
      }
    }
  };


  return (
    <article className="chart-card">
      <div className="chart-card-header">
        <div className="chart-card-title">
          <h2>Average Time per Charge</h2>

          <p>
            Hold <strong>Ctrl</strong> and scroll to zoom, or hold{" "}
            <strong>Shift</strong> and drag to zoom into a region.
          </p>
        </div>


        <ChartToolbar
          chartRef={chartRef}
          title="Average Time per Charge"
        />
      </div>


      {/* Render the average charging-time bar chart */}
      <div className="chart-canvas-container">
        <Bar
          ref={chartRef}
          data={chartData}
          options={options}
        />
      </div>
    </article>
  );
}


export default AverageTimeChart;